import { useState } from 'react';
import {
  Banknote,
  CreditCard,
  Globe2,
  HelpCircle,
  Info,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import type {
  CurrencyCode,
  GatewayMode,
  GatewaySlug,
  PaymentGatewayDetailed,
} from '../../mocks/clientBatch';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrencies, usePaymentGateways, updateGateway } from '../../api/platform';

const GATEWAY_ICON: Record<GatewaySlug, LucideIcon> = {
  sslcommerz: Wallet,
  stripe: CreditCard,
  paypal: Globe2,
  manual_cash: Banknote,
};

const GATEWAY_ACCENT: Record<GatewaySlug, string> = {
  sslcommerz:
    'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300',
  stripe:
    'bg-violet-100 dark:bg-violet-950/60 text-violet-700 dark:text-violet-300',
  paypal:
    'bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300',
  manual_cash:
    'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
};

const MODE_DOT: Record<GatewayMode, string> = {
  live: 'bg-emerald-500',
  sandbox: 'bg-amber-500',
  disabled: 'bg-zinc-400 dark:bg-zinc-500',
};

const MODE_LABEL: Record<GatewayMode, string> = {
  live: 'Connected',
  sandbox: 'Test mode',
  disabled: 'Disabled',
};

const MODE_TEXT: Record<GatewayMode, string> = {
  live: 'text-emerald-700 dark:text-emerald-300',
  sandbox: 'text-amber-700 dark:text-amber-300',
  disabled: 'text-zinc-500 dark:text-zinc-400',
};

/**
 * Payment gateways — three tile grid (SSLCommerz / Stripe / PayPal) plus
 * the manual cash row. Each tile shows mode, supported currencies, last
 * charge, this-month volume, and a Configure drawer.
 */
export function Gateways() {
  const { data: paymentGatewaysDetailed = [] } = usePaymentGateways();
  const queryClient = useQueryClient();
  // Show the three external gateways as tiles. Manual cash is documented
  // in the routing card below — its config is at /admin/cash-entry.
  const tileGateways = paymentGatewaysDetailed.filter(
    (g) => g.slug !== 'manual_cash',
  );

  const [openSlug, setOpenSlug] = useState<GatewaySlug | null>(null);
  const activeGateway =
    paymentGatewaysDetailed.find((g) => g.slug === openSlug) ?? null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Payment gateways"
        subtitle="Configure how customers can pay. Currency drives gateway routing at checkout."
      />

      {/* Three tiles */}
      <div className="grid sm:grid-cols-3 gap-4">
        {tileGateways.map((g) => (
          <GatewayTile
            key={g.slug}
            gateway={g}
            onConfigure={() => setOpenSlug(g.slug)}
          />
        ))}
      </div>

      {/* Routing rule info card */}
      <Card className="mt-6 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="shrink-0 w-9 h-9 grid place-items-center rounded-md bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300">
            <Info className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
              Checkout routing rule
            </h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              At checkout, the platform picks a gateway based on the invoice's currency:
            </p>
            <ul className="mt-2.5 text-sm text-zinc-700 dark:text-zinc-200 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-zinc-400 dark:text-zinc-500">•</span>
                <span>
                  <span className="font-medium">BDT</span> → SSLCommerz only (manual cash also available)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-zinc-400 dark:text-zinc-500">•</span>
                <span>
                  <span className="font-medium">USD / GBP / EUR</span> → Stripe primary, PayPal as fallback
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Configure drawer */}
      <Drawer
        open={openSlug !== null}
        onClose={() => setOpenSlug(null)}
        title={
          activeGateway
            ? `Configure ${activeGateway.displayName}`
            : 'Configure gateway'
        }
        width="md"
      >
        {activeGateway && (
          <ConfigureForm
            gateway={activeGateway}
            onClose={() => setOpenSlug(null)}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['admin', 'gateways'] });
              setOpenSlug(null);
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function GatewayTile({
  gateway,
  onConfigure,
}: {
  gateway: PaymentGatewayDetailed;
  onConfigure: () => void;
}) {
  const Icon = GATEWAY_ICON[gateway.slug];

  // Pick a primary currency for the this-month total. Prefer the gateway's
  // declared primary; fall back to the first supported currency.
  const primaryCurrency: CurrencyCode =
    gateway.isPrimaryFor?.[0] ?? gateway.supportedCurrencies[0] ?? 'BDT';

  return (
    <Card className="p-5 sm:p-6 flex flex-col">
      {/* Header: icon + name + mode badge */}
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'shrink-0 w-10 h-10 grid place-items-center rounded-md',
            GATEWAY_ACCENT[gateway.slug],
          )}
        >
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            {gateway.displayName}
          </h3>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className={cn(
                'inline-block w-1.5 h-1.5 rounded-full',
                MODE_DOT[gateway.mode],
              )}
            />
            <span className={cn('text-xs font-medium', MODE_TEXT[gateway.mode])}>
              {MODE_LABEL[gateway.mode]}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {gateway.description}
      </p>

      {/* Currencies */}
      <div className="mt-4">
        <span className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          Currencies
        </span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {gateway.supportedCurrencies.map((code) => (
            <CurrencyPill key={code} code={code} />
          ))}
        </div>
      </div>

      {/* Last charge */}
      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700/70 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="text-zinc-500 dark:text-zinc-400">Last charge</span>
          {gateway.lastChargeAt &&
          gateway.lastChargeAmount !== null &&
          gateway.lastChargeCurrency ? (
            <span className="flex items-center gap-2 text-zinc-700 dark:text-zinc-200">
              <span className="tabular-nums text-xs">
                {formatDate(gateway.lastChargeAt, { compact: true })}
              </span>
              <Money
                minor={gateway.lastChargeAmount}
                currencyCode={gateway.lastChargeCurrency}
                hideCents
                tone="neutral"
                className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
              />
            </span>
          ) : (
            <span className="text-zinc-500 dark:text-zinc-400 text-xs">
              No charges yet
            </span>
          )}
        </div>

        <div className="mt-2 flex items-start justify-between gap-2">
          <span className="text-zinc-500 dark:text-zinc-400">This month</span>
          <div className="text-right">
            <div className="text-zinc-700 dark:text-zinc-200 text-xs tabular-nums">
              {gateway.totalChargesThisMonth.toLocaleString('en-US')} charges
            </div>
            {gateway.totalAmountThisMonth > 0 ? (
              <Money
                minor={gateway.totalAmountThisMonth}
                currencyCode={primaryCurrency}
                hideCents
                tone="neutral"
                className="text-sm font-semibold text-zinc-900 dark:text-zinc-100"
              />
            ) : (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">—</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <Button variant="secondary" size="sm" fullWidth onClick={onConfigure}>
          Configure
        </Button>
      </div>
    </Card>
  );
}

function CurrencyPill({ code }: { code: CurrencyCode }) {
  const { data: currencies = [] } = useCurrencies();
  const meta = currencies.find((c) => c.code === code);
  if (!meta) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700">
      <span aria-hidden="true">{meta.flag}</span>
      <span>{meta.code}</span>
    </span>
  );
}

/* ── Configure form ─────────────────────────────────────────────────── */

function ConfigureForm({
  gateway,
  onClose,
  onSaved,
}: {
  gateway: PaymentGatewayDetailed & { configMasked?: Record<string, string>; configuredKeys?: string[] };
  onClose: () => void;
  onSaved: () => void;
}) {
  const fields = GATEWAY_FIELDS[gateway.slug] ?? [];
  const [mode, setMode] = useState<GatewayMode>(gateway.mode);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const config = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''));
      await updateGateway(gateway.slug, { mode, config });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the gateway configuration.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        {gateway.description}
      </p>

      {gateway.slug === 'manual_cash' ? (
        <>
          <ManualCashInfo />
          <div className="pt-2 flex items-center justify-end">
            <Button type="button" variant="secondary" size="md" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <>
          {fields.map((f) => (
            <Input
              key={f.key}
              label={f.label}
              type={f.secret ? 'password' : 'text'}
              value={values[f.key] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              placeholder={gateway.configMasked?.[f.key] ?? f.placeholder ?? ''}
              hint={gateway.configMasked?.[f.key] ? 'Saved — leave blank to keep the stored value.' : f.hint}
            />
          ))}

          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as GatewayMode)}
              className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="live">Live</option>
              <option value="sandbox">Sandbox / test</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>

          {gateway.slug === 'sslcommerz' && (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Checkout currently signs with the server-environment SSLCommerz credentials; values saved here are
              stored encrypted and take over when config-driven routing ships. IPN endpoint:{' '}
              <code className="mono px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200">
                /payments/sslcommerz/ipn
              </code>
            </p>
          )}

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
          )}

          <div className="pt-2 flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="md" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="md" loading={saving}>
              Save changes
            </Button>
          </div>
        </>
      )}
    </form>
  );
}

const GATEWAY_FIELDS: Record<string, Array<{ key: string; label: string; secret?: boolean; placeholder?: string; hint?: string }>> = {
  sslcommerz: [
    { key: 'storeId', label: 'Store ID' },
    { key: 'storePassword', label: 'Store password', secret: true },
    { key: 'merchantId', label: 'Merchant ID (optional)' },
  ],
  stripe: [
    { key: 'publishableKey', label: 'Publishable key', placeholder: 'pk_live_… or pk_test_…' },
    { key: 'secretKey', label: 'Secret key', secret: true, placeholder: 'sk_…' },
    { key: 'webhookSecret', label: 'Webhook signing secret', secret: true, placeholder: 'whsec_…' },
  ],
  paypal: [
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client secret', secret: true },
    { key: 'webhookId', label: 'Webhook ID' },
  ],
};

function ManualCashInfo() {
  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-800/70 bg-amber-50/60 dark:bg-amber-950/30 p-4">
      <div className="flex items-start gap-3">
        <HelpCircle className="w-4 h-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
        <div className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
          Manual cash entries are recorded by the platform admin via{' '}
          <code className="mono px-1.5 py-0.5 rounded bg-amber-100/80 dark:bg-amber-900/50">
            /admin/cash-entry
          </code>
          . No external configuration.
        </div>
      </div>
    </div>
  );
}
